import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Search } from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MemberCard from "@/components/MemberCard";
import { useSeo } from "@/lib/seo";

const EMPTY_FILTERS = { categories: [], states: [], districts: [], cities: [], areas: [] };
const createInitialForm = (category = "all") => ({
  q: "", category, state: "all", district: "all", city: "all", area: "all",
});
export default function Directory() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category") || "all";
  const initialForm = createInitialForm(initialCategory);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [form, setForm] = useState(initialForm);
  const [applied, setApplied] = useState(initialForm);
  const [showFilters, setShowFilters] = useState(initialCategory !== "all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchFocused, setSearchFocused] = useState(false);

  const categorySuggestions = useMemo(() => {
    const needle = form.q.trim().toLowerCase();
    if (!needle) return [];
    return [...filters.categories]
      .filter((name) => name.toLowerCase().includes(needle))
      .sort((a, b) => {
        const aStarts = a.toLowerCase().startsWith(needle);
        const bStarts = b.toLowerCase().startsWith(needle);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return a.localeCompare(b);
      })
      .slice(0, 8);
  }, [filters.categories, form.q]);

  useSeo({
    title: "Find Photographers & Videographers Near You | PRONET",
    description: "Search PRONET's Pro ID directory of photographers and videographers by category, state, district, city and area.",
  });

  useEffect(() => {
    const params = {};
    if (form.state !== "all") params.state = form.state;
    if (form.district !== "all") params.district = form.district;
    if (form.city !== "all") params.city = form.city;
    api.get("/public/filters", { params })
      .then((r) => setFilters({ ...EMPTY_FILTERS, ...r.data }))
      .catch(() => {});
  }, [form.state, form.district, form.city]);

  const fetchMembers = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 24 };
    Object.entries(applied).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
    api.get("/public/members", { params })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [applied, page]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);
  const submit = (e) => {
    e.preventDefault();
    setSearchFocused(false);
    setShowFilters(true);
    setPage(1);
    setApplied(form);
  };

  const clearFilters = () => {
    const reset = createInitialForm();
    setForm(reset);
    setApplied(reset);
    setPage(1);
  };

  return (
    <div className="relative container-wide overflow-x-hidden py-14 md:py-20">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -top-20 -left-20 opacity-40" />
        <div className="blob blob-magenta w-[400px] h-[400px] top-60 right-10 opacity-30" style={{ animationDelay: "-10s" }} />
      </div>

      <div className="relative z-10 mb-10 fade-up">
        <div className="overline text-fuchsia-300">GoMore</div>
        <h1 className="mt-3 font-display text-5xl leading-[0.95] sm:text-6xl md:text-7xl">
          Pro ID <span className="italic text-gradient">professionals</span>
        </h1>
        <p className="text-white/60 mt-4 max-w-2xl">
          Browse photographers, videographers and creative professionals with a PRONET Pro ID.
        </p>
      </div>

      <form onSubmit={submit} className="relative z-20 glass rounded-2xl p-4 md:p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(210px,0.42fr)_auto]">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="search-input"
              className="pl-9 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40"
              placeholder="Search category, name, studio, member ID or location…"
              value={form.q}
              onChange={(e) => setForm({ ...form, q: e.target.value })}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => window.setTimeout(() => setSearchFocused(false), 100)}
              autoComplete="off"
            />
            {searchFocused && categorySuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-[#171720] p-2 shadow-2xl" data-testid="category-suggestions">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">Matching categories</div>
                {categorySuggestions.map((name) => (
                  <button key={name} type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => { setForm({ ...form, q: name }); setSearchFocused(false); }}
                    data-testid={`category-suggestion-${name}`}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm text-white/80 hover:bg-violet-500/15 hover:text-white">
                    <span>{name}</span>
                    <span className="rounded-full border border-violet-400/25 bg-violet-500/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-violet-200">Category</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <FilterSelect testid="search-category" value={form.category} label="All categories"
            options={filters.categories} onChange={(category) => setForm({ ...form, category })} />
          <button
            type="submit"
            data-testid="apply-filters"
            className="h-11 shrink-0 btn-glow rounded-xl px-6 text-white font-semibold"
          >
            Search
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 border-t border-white/10 pt-4" data-testid="advanced-filters">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <FilterSelect testid="filter-state" value={form.state} label="All states"
                options={filters.states} onChange={(state) => setForm({ ...form, state, district: "all", city: "all", area: "all" })} />
              <FilterSelect testid="filter-district" value={form.district} label="All districts"
                options={filters.districts} onChange={(district) => setForm({ ...form, district, city: "all", area: "all" })} />
              <FilterSelect testid="filter-city" value={form.city} label="All cities"
                options={filters.cities} onChange={(city) => setForm({ ...form, city, area: "all" })} />
              <FilterSelect testid="filter-area" value={form.area} label="All areas"
                options={filters.areas} onChange={(area) => setForm({ ...form, area })} />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-xs text-white/40">Choose any combination, then press Search to apply.</span>
              <button type="button" onClick={clearFilters} data-testid="clear-filters"
                className="text-xs font-medium text-white/55 hover:text-fuchsia-300">
                Clear filters
              </button>
            </div>
          </div>
        )}
      </form>

      <div className="relative mt-6 text-sm text-white/50" data-testid="results-count">
        {loading ? "Loading…" : `${data.total} member${data.total === 1 ? "" : "s"} found`}
      </div>

      <div className="relative mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        {data.items.map((m, i) => (
          <MemberCard key={m.id} member={m} index={i} />
        ))}
        {!loading && data.items.length === 0 && (
          <div className="col-span-full text-center py-24 text-white/50" data-testid="empty-directory">
            No members match your filters yet. Try broadening your search.
          </div>
        )}
      </div>

      {data.total > 24 && (
        <div className="relative z-10 mt-10 flex items-center justify-center gap-2">
          <button
            className="btn-ghost px-4 py-2 rounded-full disabled:opacity-40"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="page-prev"
          >Previous</button>
          <span className="text-sm text-white/60">Page {page}</span>
          <button
            className="btn-ghost px-4 py-2 rounded-full disabled:opacity-40"
            disabled={page * 24 >= data.total}
            onClick={() => setPage((p) => p + 1)}
            data-testid="page-next"
          >Next</button>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ testid, value, label, options, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-11 bg-white/5 border-white/10 text-white">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{label}</SelectItem>
        {options.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
